import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Plus } from "lucide-react";
import { api, asList } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { mediaUrl } from "@/lib/site";
import { Eyebrow, FadeUp, MaskLine } from "./Reveal";
import { InquireDialog } from "./InquireDialog";

const FALLBACK = "/img/hoodie-front.jpg";
const gallery = (p) => [p.image, ...(Array.isArray(p.images) ? p.images : [])].filter(Boolean).map(mediaUrl);
const priceText = (p, t) => (/^\d/.test(p.price || "") ? `${p.price} €` : t(`price.${p.price}`) === `price.${p.price}` ? p.price : t(`price.${p.price}`));
const statusText = (s, t) => (t(`status.${s}`) === `status.${s}` ? s : t(`status.${s}`));

const Featured = ({ p, onInquire }) => {
  const { t, lang } = useLang();
  const imgs = gallery(p).length ? gallery(p) : [FALLBACK];
  const [idx, setIdx] = useState(0);
  const desc = (lang === "pt" && p.description_pt) || p.description;
  const sizes = Array.isArray(p.sizes) ? p.sizes : [];
  return (
    <div data-testid={`product-featured-${p.id}`} className="grid lg:grid-cols-12 gap-8 lg:gap-12">
      <div className="lg:col-span-7 grid grid-cols-[1fr_auto] gap-3">
        <div className="group relative clip-cut overflow-hidden aspect-[4/5] bg-[var(--ink-3)]">
          <AnimatePresence initial={false}>
            <motion.img key={imgs[idx]} src={imgs[idx]} alt={p.name}
              initial={{ opacity: 0, scale: 1.06 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 transition-[filter] duration-700" />
          </AnimatePresence>
          <span className="absolute top-4 left-4 font-mono text-[11px] tracking-[0.2em] bg-black/70 px-2 py-1">{String(idx + 1).padStart(2, "0")} / {String(imgs.length).padStart(2, "0")}</span>
        </div>
        <div className="flex flex-col gap-3 w-16 sm:w-24">
          {imgs.map((src, i) => (
            <button key={src} data-testid={`product-thumb-${i}`} onClick={() => setIdx(i)} aria-label={`${t("gear.photo")} ${i + 1}`}
              className={`group aspect-[3/4] overflow-hidden border transition-[opacity,border-color] ${i === idx ? "border-[var(--bone)] opacity-100" : "border-transparent opacity-50 hover:opacity-90"}`}>
              <img src={src} alt="" className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-[filter] duration-500" />
            </button>
          ))}
        </div>
      </div>
      <div className="lg:col-span-5 flex flex-col justify-center">
        <div className="flex items-center gap-3 font-mono text-[11px] tracking-[0.2em] text-[var(--muted)]">
          <span>{p.sku}</span><span className="w-6 h-px bg-[var(--line)]" />
          <span data-testid={`product-status-${p.id}`} className="bg-[var(--bone)] text-black px-2 py-1">{statusText(p.status, t)}</span>
        </div>
        <h3 className="font-display font-black uppercase text-5xl sm:text-7xl leading-[0.85] mt-6">{p.name}</h3>
        <p data-testid={`product-price-${p.id}`} className="font-mono text-lg mt-5">{priceText(p, t)}</p>
        {desc && <p className="text-[var(--muted)] leading-relaxed mt-5 max-w-md">{desc}</p>}
        {sizes.length > 0 && (
          <div data-testid={`product-sizes-${p.id}`} className="mt-6">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--muted)]">{t("gear.sizes")}</p>
            <div className="flex flex-wrap gap-2 mt-2">
              {sizes.map((s) => <span key={s} className="border border-[var(--line)] px-3 py-1.5 font-mono text-xs">{s}</span>)}
            </div>
          </div>
        )}
        <button data-testid={`product-inquire-${p.id}`} onClick={() => onInquire(p)}
          className="group mt-10 self-start clip-cut-sm bg-[var(--signal)] text-black font-mono text-xs uppercase tracking-[0.25em] px-8 py-4 inline-flex items-center gap-3 hover:bg-[#cfcfcf] transition-colors">
          {t("gear.inquire")} <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform" />
        </button>
        <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--muted)]">{t("gear.note")}</p>
      </div>
    </div>
  );
};

const Card = ({ p, onInquire }) => {
  const { t } = useLang();
  return (
  <button data-testid={`product-card-${p.id}`} onClick={() => onInquire(p)}
    className="group relative w-full aspect-[4/5] text-left clip-cut overflow-hidden bg-[var(--ink-3)]">
    <img src={gallery(p)[0] || FALLBACK} alt={p.name}
      className="absolute inset-0 w-full h-full object-cover grayscale brightness-[0.7] group-hover:grayscale-0 group-hover:brightness-90 group-hover:scale-105 transition-[filter,transform] duration-700" />
    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/10 to-transparent" />
    <span className="absolute top-4 right-4 font-mono text-[10px] tracking-[0.2em] px-2 py-1 bg-[var(--bone)] text-black">{statusText(p.status, t)}</span>
    <div className="absolute bottom-0 inset-x-0 p-5 flex items-end justify-between gap-4">
      <div>
        <h3 className="font-display font-extrabold uppercase text-3xl leading-[0.9]">{p.name}</h3>
        <p data-testid={`product-price-${p.id}`} className="font-mono text-sm mt-2">{priceText(p, t)}</p>
      </div>
      <span className="w-11 h-11 border border-white/40 grid place-items-center group-hover:bg-white group-hover:text-black transition-colors">
        <Plus className="w-5 h-5 group-hover:rotate-90 transition-transform duration-500" />
      </span>
    </div>
  </button>
  );
};

export const Gear = () => {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [inquire, setInquire] = useState(null);
  const [selId, setSelId] = useState(null);
  const featuredRef = useRef(null);
  useEffect(() => {
    api.get("/products").then((r) => setItems(asList(r).filter((p) => p.visible !== false))).catch(() => setItems([]));
  }, []);
  const first = items.find((p) => p.id === selId) || items[0];
  const rest = items.filter((p) => p !== first);
  const select = (p) => {
    setSelId(p.id);
    const el = featuredRef.current;
    if (el) (window.__lenis ? window.__lenis.scrollTo(el, { offset: -100 }) : el.scrollIntoView({ behavior: "smooth" }));
  };

  return (
    <section id="gear" data-testid="gear-catalog-section" className="px-5 sm:px-8 lg:px-12 py-24 sm:py-36 bg-[var(--ink-2)] border-y border-[var(--line)]">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14">
        <div>
          <Eyebrow>{t("gear.eyebrow")}</Eyebrow>
          <h2 className="font-display font-black uppercase text-5xl sm:text-7xl lg:text-8xl leading-[0.85] mt-5">
            <MaskLine>{t("gear.title")[0]}</MaskLine>
            <MaskLine delay={0.1}>{t("gear.title")[1]}</MaskLine>
          </h2>
        </div>
        <FadeUp className="max-w-sm text-sm sm:text-base text-[var(--muted)] leading-relaxed">
          {t("gear.intro")}
        </FadeUp>
      </div>

      {!first ? (
        <p data-testid="gear-empty" className="font-mono text-sm text-[var(--muted)] py-20 text-center">{t("gear.empty")}</p>
      ) : (
        <FadeUp><div ref={featuredRef}><Featured key={first.id} p={first} onInquire={setInquire} /></div></FadeUp>
      )}
      {rest.length > 0 && (
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {rest.map((p, i) => <FadeUp key={p.id} delay={(i % 3) * 0.08}><Card p={p} onInquire={select} /></FadeUp>)}
        </div>
      )}

      <InquireDialog open={!!inquire} onOpenChange={(o) => !o && setInquire(null)} type="merch" subject={inquire?.name || ""} />
    </section>
  );
};
